import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Card, CardContent } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Plus, Trash2, Edit2, Globe, Copy, Check, GripVertical,
  Eye, ExternalLink, FileText, ChevronUp, ChevronDown,
  ToggleLeft, Users, Mail, MessageSquare, Zap, Code2
} from "lucide-react";

const AVAILABLE_FIELDS = [
  { key: "full_name",       label: "Full Name",       type: "text",     defaultRequired: true },
  { key: "phone",           label: "Phone",           type: "tel",      defaultRequired: true },
  { key: "email",           label: "Email",           type: "email",    defaultRequired: false },
  { key: "address",         label: "Address",         type: "text",     defaultRequired: false },
  { key: "service_type",    label: "Service Type",    type: "text",     defaultRequired: false },
  { key: "description",     label: "Description",     type: "textarea", defaultRequired: false },
  { key: "preferred_date",  label: "Preferred Date",  type: "date",     defaultRequired: false },
];

const JOB_STATUSES = [
  { value: "scheduled", label: "Scheduled" },
  { value: "pending", label: "Pending" },
  { value: "new", label: "New" },
];

const LeadFormsSection = ({ isDark, headers }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedForm, setEmbedForm] = useState(null);
  const [technicians, setTechnicians] = useState([]);

  const fetchForms = useCallback(async () => {
    try {
      const [formsRes, techRes] = await Promise.all([
        axios.get(`${API}/lead-forms`, { headers }),
        axios.get(`${API}/technicians`, { headers }).catch(() => ({ data: [] })),
      ]);
      setForms(formsRes.data || []);
      setTechnicians(techRes.data || []);
    } catch { toast.error("Failed to load forms"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const toggleForm = async (formId) => {
    try {
      const res = await axios.patch(`${API}/lead-forms/${formId}/toggle`, {}, { headers });
      setForms(prev => prev.map(f => f.form_id === formId ? { ...f, is_active: res.data.is_active } : f));
      toast.success(res.data.is_active ? "Form activated" : "Form deactivated");
    } catch { toast.error("Failed to toggle form"); }
  };

  const deleteForm = async (formId) => {
    if (!window.confirm("Delete this form and its embed code?")) return;
    try {
      await axios.delete(`${API}/lead-forms/${formId}`, { headers });
      setForms(prev => prev.filter(f => f.form_id !== formId));
      toast.success("Form deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const openCreate = () => {
    setEditingForm(null);
    setEditOpen(true);
  };

  const openEdit = (form) => {
    setEditingForm(form);
    setEditOpen(true);
  };

  const openEmbed = (form) => {
    setEmbedForm(form);
    setEmbedOpen(true);
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="lead-forms-section">
      {/* Create CTA */}
      <button
        onClick={openCreate}
        className="w-full rounded-xl py-3.5 px-5 flex items-center justify-between text-black font-semibold text-sm transition-all hover:shadow-lg bg-gradient-to-r from-[#FFCC00] to-[#FFD60A] hover:scale-[1.005] active:scale-[0.995]"
        data-testid="create-form-btn"
      >
        <div className="flex items-center gap-2">
          <Plus className="w-5 h-5" />
          <span className="tracking-wide">CREATE NEW FORM</span>
        </div>
        <Globe className="w-5 h-5" />
      </button>

      {/* Forms list */}
      {forms.length === 0 ? (
        <Card className={isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-gray-200 shadow-sm"}>
          <CardContent className="p-10 text-center">
            <Globe className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm mb-1">No lead forms created yet</p>
            <p className="text-muted-foreground/60 text-xs">Create a form to embed on your website and start collecting leads automatically</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {forms.map((form, i) => (
            <div
              key={form.form_id}
              className={`group rounded-xl border p-5 transition-all ${
                form.is_active
                  ? isDark ? "border-[#FFCC00]/30 bg-[#FFCC00]/[0.03]" : "border-[#FFCC00]/40 bg-[#FFCC00]/[0.02] shadow-sm"
                  : isDark ? "border-white/10 bg-white/[0.02] opacity-60" : "border-gray-200 bg-white opacity-60 shadow-sm"
              }`}
              data-testid={`form-card-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.is_active ? "bg-[#FFCC00]/20" : "bg-gray-500/15"}`}>
                      <Globe className={`w-4 h-4 ${form.is_active ? "text-[#FFCC00]" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground truncate">{form.name}</h4>
                      <p className="text-[10px] text-muted-foreground">{form.fields?.length || 0} fields</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 text-muted-foreground border-border">
                      {form.submissions_count || 0} submissions
                    </Badge>
                    {form.settings?.notify_via_email && (
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-0.5 text-muted-foreground border-border">
                        <Mail className="w-2.5 h-2.5" /> Email
                      </Badge>
                    )}
                    {form.settings?.notify_via_sms && (
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-0.5 text-muted-foreground border-border">
                        <MessageSquare className="w-2.5 h-2.5" /> SMS
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Switch checked={form.is_active} onCheckedChange={() => toggleForm(form.form_id)} data-testid={`toggle-form-${i}`} />
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => openEdit(form)} title="Edit" data-testid={`edit-form-${i}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-500" onClick={() => openEmbed(form)} title="Embed Code" data-testid={`embed-form-${i}`}>
                      <Code2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500" onClick={() => deleteForm(form.form_id)} data-testid={`delete-form-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Builder Dialog */}
      <FormBuilderDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        isDark={isDark}
        headers={headers}
        editingForm={editingForm}
        technicians={technicians}
        onSaved={fetchForms}
      />

      {/* Embed Code Dialog */}
      <EmbedCodeDialog
        open={embedOpen}
        onClose={() => setEmbedOpen(false)}
        isDark={isDark}
        form={embedForm}
      />
    </div>
  );
};

/* ──── Form Builder Dialog ──── */
const FormBuilderDialog = ({ open, onClose, isDark, headers, editingForm, technicians, onSaved }) => {
  const isEditing = !!editingForm;
  const [name, setName] = useState("");
  const [fields, setFields] = useState([]);
  const [settings, setSettings] = useState({
    default_status: "scheduled",
    default_technician_id: "",
    notify_owner: true,
    notify_admins: true,
    notify_dispatchers: false,
    notify_via_email: true,
    notify_via_sms: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editingForm) {
      setName(editingForm.name || "");
      setFields(editingForm.fields || []);
      setSettings(editingForm.settings || {});
    } else {
      setName("");
      setFields(AVAILABLE_FIELDS.map((f, i) => ({
        key: f.key, label: f.label, type: f.type, required: f.defaultRequired, order: i, options: [],
      })));
      setSettings({
        default_status: "scheduled", default_technician_id: "",
        notify_owner: true, notify_admins: true, notify_dispatchers: false,
        notify_via_email: true, notify_via_sms: false,
      });
    }
  }, [open, editingForm]);

  const toggleField = (key) => {
    const exists = fields.find(f => f.key === key);
    if (exists) {
      if (fields.length <= 1) { toast.error("Must have at least 1 field"); return; }
      setFields(prev => prev.filter(f => f.key !== key));
    } else {
      const tmpl = AVAILABLE_FIELDS.find(f => f.key === key);
      setFields(prev => [...prev, { key: tmpl.key, label: tmpl.label, type: tmpl.type, required: tmpl.defaultRequired, order: prev.length, options: [] }]);
    }
  };

  const toggleRequired = (key) => {
    setFields(prev => prev.map(f => f.key === key ? { ...f, required: !f.required } : f));
  };

  const moveField = (idx, dir) => {
    const newFields = [...fields];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= newFields.length) return;
    [newFields[idx], newFields[newIdx]] = [newFields[newIdx], newFields[idx]];
    setFields(newFields.map((f, i) => ({ ...f, order: i })));
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Form name is required"); return; }
    if (fields.length === 0) { toast.error("Add at least one field"); return; }
    setSubmitting(true);
    try {
      const payload = { name, fields, settings };
      if (isEditing) {
        await axios.put(`${API}/lead-forms/${editingForm.form_id}`, payload, { headers });
        toast.success("Form updated");
      } else {
        await axios.post(`${API}/lead-forms`, payload, { headers });
        toast.success("Form created");
      }
      onClose();
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="form-builder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#FFCC00]" />
            {isEditing ? "Edit Form" : "Create Lead Form"}
          </DialogTitle>
          <DialogDescription>Configure fields and settings for your embeddable form</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Form Name */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Form Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Website Contact Form" data-testid="form-name-input" />
          </div>

          {/* Fields */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">Fields</Label>
            <div className="space-y-1.5">
              {fields.map((field, i) => (
                <div
                  key={field.key}
                  className={`flex items-center gap-2 p-2.5 rounded-lg ${isDark ? "bg-zinc-800/50" : "bg-gray-50"}`}
                  data-testid={`field-row-${field.key}`}
                >
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveField(i, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-20 p-0.5">
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button onClick={() => moveField(i, 1)} disabled={i === fields.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-20 p-0.5">
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                  <GripVertical className="w-3.5 h-3.5 text-muted-foreground/30 flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground flex-1">{field.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? "bg-zinc-700 text-zinc-400" : "bg-gray-200 text-gray-500"}`}>{field.type}</span>
                  <button
                    onClick={() => toggleRequired(field.key)}
                    className={`text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors ${
                      field.required
                        ? "bg-red-500/15 text-red-500 hover:bg-red-500/25"
                        : isDark ? "bg-zinc-700 text-zinc-400 hover:bg-zinc-600" : "bg-gray-200 text-gray-400 hover:bg-gray-300"
                    }`}
                  >
                    {field.required ? "Required" : "Optional"}
                  </button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-red-500" onClick={() => toggleField(field.key)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Add fields */}
            {AVAILABLE_FIELDS.filter(af => !fields.find(f => f.key === af.key)).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {AVAILABLE_FIELDS.filter(af => !fields.find(f => f.key === af.key)).map(af => (
                  <button
                    key={af.key}
                    onClick={() => toggleField(af.key)}
                    className={`text-xs px-2.5 py-1 rounded-full flex items-center gap-1 transition-colors ${
                      isDark ? "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    <Plus className="w-2.5 h-2.5" /> {af.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Settings */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">Job Settings</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Default Status</Label>
                <Select value={settings.default_status} onValueChange={v => setSettings(s => ({ ...s, default_status: v }))}>
                  <SelectTrigger className="h-9" data-testid="form-default-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JOB_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Assign Technician</Label>
                <Select value={settings.default_technician_id || "none"} onValueChange={v => setSettings(s => ({ ...s, default_technician_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="h-9" data-testid="form-default-tech"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {technicians.map(t => <SelectItem key={t.technician_id} value={t.technician_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">Notifications</Label>
            <div className={`rounded-lg p-3 space-y-3 ${isDark ? "bg-zinc-800/30 border border-zinc-700" : "bg-gray-50 border border-gray-200"}`}>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "notify_owner", label: "Owner", icon: Users },
                  { key: "notify_admins", label: "Admins", icon: Users },
                  { key: "notify_dispatchers", label: "Dispatchers", icon: Users },
                ].map(n => (
                  <label key={n.key} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings[n.key] || false}
                      onChange={e => setSettings(s => ({ ...s, [n.key]: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-xs text-foreground">{n.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-4 pt-2 border-t" style={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb" }}>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={settings.notify_via_email || false} onChange={e => setSettings(s => ({ ...s, notify_via_email: e.target.checked }))} className="rounded" />
                  <Mail className="w-3 h-3 text-muted-foreground" />
                  <span className="text-xs text-foreground">Email</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={settings.notify_via_sms || false} onChange={e => setSettings(s => ({ ...s, notify_via_sms: e.target.checked }))} className="rounded" />
                  <MessageSquare className="w-3 h-3 text-muted-foreground" />
                  <span className="text-xs text-foreground">SMS</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={submitting} className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90" data-testid="save-form-btn">
            {submitting ? "Saving..." : isEditing ? "Update Form" : "Create Form"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ──── Embed Code Dialog ──── */
const EmbedCodeDialog = ({ open, onClose, isDark, form }) => {
  const [copied, setCopied] = useState(null);
  if (!form) return null;

  const baseUrl = window.location.origin;
  const directUrl = `${baseUrl}/form/${form.form_id}`;
  const iframeCode = `<iframe src="${directUrl}" width="100%" height="650" frameborder="0" style="border:none;border-radius:16px;max-width:520px;margin:0 auto;display:block;"></iframe>`;
  const jsCode = `<div id="fieldz-form-${form.form_id}"></div>\n<script>\n(function(){var d=document,f=d.createElement('iframe');f.src='${directUrl}';f.style.cssText='width:100%;height:650px;border:none;border-radius:16px;max-width:520px;margin:0 auto;display:block';d.getElementById('fieldz-form-${form.form_id}').appendChild(f)})();\n</script>`;

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="embed-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code2 className="w-5 h-5 text-[#FFCC00]" />
            Embed Code — {form.name}
          </DialogTitle>
          <DialogDescription>Copy any of the options below to add this form to your website</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Direct URL */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5 block">Direct Link</Label>
            <div className="flex gap-2">
              <Input value={directUrl} readOnly className="text-xs font-mono flex-1" data-testid="direct-url" />
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(directUrl, "url")} data-testid="copy-url">
                {copied === "url" ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(directUrl, "_blank")} data-testid="open-form">
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Iframe */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5 block">Iframe Embed</Label>
            <p className="text-[11px] text-muted-foreground mb-2">Works with WordPress, Wix, Squarespace, and any HTML page</p>
            <div className="relative">
              <pre className={`text-xs font-mono p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all ${isDark ? "bg-zinc-800 text-zinc-300" : "bg-gray-50 text-gray-700 border border-gray-200"}`}>
                {iframeCode}
              </pre>
              <Button variant="outline" size="sm" className="absolute top-2 right-2" onClick={() => copyToClipboard(iframeCode, "iframe")} data-testid="copy-iframe">
                {copied === "iframe" ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>

          {/* JS Snippet */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5 block">JavaScript Snippet</Label>
            <p className="text-[11px] text-muted-foreground mb-2">Best for WordPress Elementor, Gutenberg blocks, or custom HTML</p>
            <div className="relative">
              <pre className={`text-xs font-mono p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all ${isDark ? "bg-zinc-800 text-zinc-300" : "bg-gray-50 text-gray-700 border border-gray-200"}`}>
                {jsCode}
              </pre>
              <Button variant="outline" size="sm" className="absolute top-2 right-2" onClick={() => copyToClipboard(jsCode, "js")} data-testid="copy-js">
                {copied === "js" ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadFormsSection;
